<script setup lang="ts">
import { computed, watch } from 'vue';

import { useRoute } from 'vue-router';

import { LpProductsMenu, LpMeetErnestCta } from '@thehotelsnetwork/lampo';
import { useFeatureFlags } from '@thehotelsnetwork/vue-feature-flags';
import { useUserTracking } from '@thehotelsnetwork/vue-user-tracking';

import HelpBtn from '@/components/HelpBtn/HelpBtn.vue';

import AccountSwitch from '@/modules/Accounts/components/AccountSwitch/AccountSwitch.vue';
import { NavigationTrackingEvent } from '@/modules/UserTracking/trackingEvents';
import { useErnestTracking } from '@/modules/UserTracking/useErnestTracking';
import { useAccountsService } from '@/modules/Accounts/services/AccountsService';
import { useUserService } from '@/modules/User/services/UserService';
import { ernestJoinUrl } from '@/config';

const route = useRoute();
const accountsService = useAccountsService();
const userService = useUserService();
const user = computed(() => userService.user);
const { flags: featureFlags } = useFeatureFlags();
const { addAction } = useUserTracking();
const {
  trackErnestButtonShown,
  trackErnestButtonClicked,
  trackErnestJoinClicked,
} = useErnestTracking();

const isNotificationsVisible = computed(
  () => userService.isTeamMember,
);

const hasMultipleAccounts = computed(() => accountsService.userHasMultipleAccounts);
const isAccountsSettingsPage = computed(() => !!route.meta?.accountSettingsPage);
const isProductsPage = computed(() => route.name === 'Products');
const isErnestCtaVisible = computed(() => !isProductsPage.value && featureFlags.value.ernest);

watch(isErnestCtaVisible, (visible) => {
  if (visible) {
    trackErnestButtonShown();
  }
}, { immediate: true });

function closeHandler(navigate: any, closeMenu: any) {
  navigate();
  closeMenu();
}

function trackProductClicked(product: string) {
  addAction(NavigationTrackingEvent.NavigationToProducts, {
    product,
  });
}
function isProductItemVisible(productId: string): boolean {
  if (productId === 'demand') {
    return featureFlags.value.demand;
  }
  if (productId === 'ernest') {
    return user.value?.active_products?.ernest && featureFlags.value.ernest;
  }
  return true;
}

</script>

<template>
  <transition
    name="slidebottom-full"
    appear
  >
    <header
      data-testid="Header"
    >
      <div class="flex">
        <div
          :class="[
            'flex space-x-4 items-center',
            isAccountsSettingsPage && 'items-center',
          ]"
        >
          <LpProductsMenu
            data-testid="products-menu"
            base-url=""
            :current-product-id="isAccountsSettingsPage ? 'settings' : ''"
            :is-visible="isProductItemVisible"
            @product-click="trackProductClicked"
          />
          <div
            v-if="isAccountsSettingsPage"
            class="flex items-center space-x-3"
          >
            <div class="text-lg font-normal tracking-widest capitalize leading-1 user-select-none">
              {{ $t('Account settings') }}
            </div>
          </div>
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <img
            v-else
            src="@/assets/imgs/thn_color.svg"
            alt="The Hotels Network"
            class="h-[3.25rem] p-2"
          >
          <!-- eslint-enable vue/no-bare-strings-in-template -->
        </div>
        <div
          v-if="isAccountsSettingsPage && hasMultipleAccounts"
          class="flex items-center mr-auto ml-20 space-x-2"
        >
          <AccountSwitch class="w-full" />
        </div>
      </div>
      <div class="flex items-center ml-auto">
        <div class="flex flex-row gap-2 items-center">
          <LpMeetErnestCta
            v-if="isErnestCtaVisible"
            :has-ernest="userService.hasErnest"
            :join-url="ernestJoinUrl"
            @show-modal="trackErnestButtonClicked"
            @open-ernest="trackErnestButtonClicked"
            @join-ernest="trackErnestJoinClicked"
          />
          <HelpBtn />
        </div>
        <LpDropdown
          v-if="user?.first_name"
          x-position="right"
          data-testid="avatarbtn"
        >
          <template #btn>
            <LpButton
              mode="transparent"
              size="small"
              color="gray"
            >
              <div
                data-testid="avatar-btn"
                class="flex flex-row items-center space-x-2 cursor-pointer"
              >
                <LpAvatar
                  :first-name="user?.first_name"
                  :last-name="user?.last_name"
                  class="font-normal"
                />
                <div class="hidden text-base font-normal lg:block">
                  {{ user?.first_name }}
                </div>
              </div>
            </LpButton>
          </template>
          <template #menu="{ closeMenu }">
            <LpCard
              rounded-size="big"
              class="px-4 py-5 mt-2 rounded lg:px-4 lg:py-5 min-w-52"
            >
              <ul class="p-0 m-0 space-y-2">
                <li>
                  <router-link
                    v-slot="{ href, navigate }"
                    to="/preferences"
                    custom
                  >
                    <LpMarkerBtn
                      :href="href"
                      @click.prevent="closeHandler(navigate, closeMenu)"
                    >
                      {{ $t('Preferences') }}
                    </LpMarkerBtn>
                  </router-link>
                </li>
                <li v-if="isNotificationsVisible">
                  <router-link
                    v-slot="{ href, navigate }"
                    to="/notifications"
                    custom
                  >
                    <LpMarkerBtn
                      :href="href"
                      data-testid="header-avatar-menu-logout"
                      @click.prevent="closeHandler(navigate, closeMenu)"
                    >
                      {{ $t('Notifications') }}
                    </LpMarkerBtn>
                  </router-link>
                </li>
                <li>
                  <router-link
                    v-slot="{ navigate }"
                    to="/logout"
                    custom
                  >
                    <LpMarkerBtn
                      data-testid="header-avatar-menu-logout"
                      @click="navigate"
                    >
                      {{ $t('Log out') }}
                    </LpMarkerBtn>
                  </router-link>
                </li>
              </ul>
            </LpCard>
          </template>
        </LpDropdown>
      </div>
    </header>
  </transition>
</template>

<style lang="postcss" scoped>
header {
  @apply fixed top-0 left-0 z-30 flex items-center justify-between w-full px-4 py-2 bg-white border-b border-base-300 min-h-[75px];
}
.user-dp-btn {
  @apply block p-2 rounded transition-colors duration-300;
  &:hover {
    @apply bg-primary-300 bg-opacity-50;
  }
}
</style>
